import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Map, Plus, Pencil, Trash2, Loader2, X } from "lucide-react";

const EMPTY = {
  name: "", region: "", duration: "6 hours", hours: 6, image: "", tagline: "", intro: "",
  itineraryText: "", includesText: "", excludes: "", faqsText: "", is_active: true,
  tiers: [
    { slug: "van", label: "Mercedes V-Class", seats: "Up to 6 guests", price: "" },
    { slug: "luxury", label: "Sprinter 11 Seater", seats: "Up to 11 guests", price: "" },
    { slug: "sprinter_14", label: "Sprinter 15 Seater", seats: "Up to 15 guests", price: "" },
    { slug: "minibus", label: "Toyota Coaster 21", seats: "Up to 21 guests", price: "" }
  ]
};

const toForm = (t) => ({
  name: t.name, region: t.region, duration: t.duration, hours: t.hours, image: t.image,
  tagline: t.tagline, intro: t.intro, excludes: t.excludes || "", is_active: t.is_active !== false,
  itineraryText: (t.itinerary || []).map((i) => `${i.time} | ${i.stop} | ${i.detail}`).join("\n"),
  includesText: (t.includes || []).join("\n"),
  faqsText: (t.faqs || []).map((f) => `${f.q} | ${f.a}`).join("\n"),
  tiers: (t.tiers || []).map((x) => ({ ...x }))
});

const toPayload = (f) => ({
  name: f.name, region: f.region, duration: f.duration, hours: parseInt(f.hours) || 1,
  image: f.image, tagline: f.tagline, intro: f.intro, excludes: f.excludes, is_active: f.is_active,
  itinerary: f.itineraryText.split("\n").filter((l) => l.trim()).map((l) => {
    const [time = "", stop = "", ...rest] = l.split("|").map((s) => s.trim());
    return { time, stop, detail: rest.join(" | ") };
  }),
  includes: f.includesText.split("\n").map((l) => l.trim()).filter(Boolean),
  faqs: f.faqsText.split("\n").filter((l) => l.includes("|")).map((l) => {
    const [q, ...rest] = l.split("|");
    return { q: q.trim(), a: rest.join("|").trim() };
  }),
  tiers: f.tiers.filter((t) => t.label && t.price !== "").map((t) => ({
    slug: t.slug || "custom", label: t.label, seats: t.seats, price: parseFloat(t.price)
  }))
});

export const TourManagerSection = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?} + form
  const [saving, setSaving] = useState(false);

  const fetchTours = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/tours/admin/all");
      setTours(res.data);
    } catch (e) {
      toast.error("Failed to load tours");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  const save = async () => {
    const payload = toPayload(editing);
    if (!payload.name || !payload.region || !payload.image) return toast.error("Name, region and image URL are required");
    if (payload.tiers.length === 0) return toast.error("Add at least one price tier");
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/tours/${editing.id}`, payload);
        toast.success("Tour updated");
      } else {
        await api.post("/tours", payload);
        toast.success("Tour created");
      }
      setEditing(null);
      fetchTours();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save tour");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}" permanently?`)) return;
    try {
      await api.delete(`/tours/${t.id}`);
      toast.success("Tour deleted");
      fetchTours();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const setTier = (i, field, value) => {
    setEditing((prev) => {
      const tiers = prev.tiers.map((t, j) => (j === i ? { ...t, [field]: value } : t));
      return { ...prev, tiers };
    });
  };

  return (
    <Card className="dashboard-card" data-testid="tour-manager-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Map className="h-5 w-5 text-primary" /> Tour Packages</CardTitle>
        <Button size="sm" onClick={() => setEditing({ ...EMPTY, tiers: EMPTY.tiers.map((t) => ({ ...t })) })} data-testid="add-tour-btn">
          <Plus className="h-4 w-4 mr-1" /> New Tour
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3">
            {tours.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-4 p-4 rounded-lg bg-secondary/30 border border-white/5" data-testid={`tour-row-${t.slug}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <img src={t.image} alt={t.name} className="w-16 h-12 rounded object-cover flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{t.name}</p>
                      {!t.is_active && <Badge className="bg-gray-500/20 text-gray-500">Hidden</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{t.region} · {t.duration} · From ${t.fromPrice} · {(t.tiers || []).length} tiers</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setEditing({ id: t.id, ...toForm(t) })} data-testid={`edit-tour-${t.slug}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => remove(t)} data-testid={`delete-tour-${t.slug}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Tour" : "New Tour"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="tour-form-name" /></div>
                <div><Label>Region</Label><Input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} data-testid="tour-form-region" /></div>
                <div><Label>Duration label</Label><Input value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} placeholder="6 hours" /></div>
                <div><Label>Hours (charter length)</Label><Input type="number" min="1" value={editing.hours} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} /></div>
              </div>
              <div><Label>Image URL</Label><Input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="https://..." data-testid="tour-form-image" /></div>
              <div><Label>Tagline</Label><Input value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} /></div>
              <div><Label>Intro paragraph</Label><Textarea rows={3} value={editing.intro} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} /></div>

              <div>
                <Label className="mb-2 block">Price tiers (per group, inc. GST)</Label>
                <div className="space-y-2">
                  {editing.tiers.map((t, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_100px_36px] gap-2 items-center">
                      <Input value={t.label} onChange={(e) => setTier(i, "label", e.target.value)} placeholder="Vehicle" />
                      <Input value={t.seats} onChange={(e) => setTier(i, "seats", e.target.value)} placeholder="Up to X guests" />
                      <Input type="number" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} placeholder="$" data-testid={`tour-tier-price-${i}`} />
                      <Button variant="ghost" size="sm" onClick={() => setEditing({ ...editing, tiers: editing.tiers.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setEditing({ ...editing, tiers: [...editing.tiers, { slug: "custom", label: "", seats: "", price: "" }] })}>
                    <Plus className="h-4 w-4 mr-1" /> Add tier
                  </Button>
                </div>
              </div>

              <div><Label>Itinerary (one stop per line: time | stop | detail)</Label>
                <Textarea rows={5} value={editing.itineraryText} onChange={(e) => setEditing({ ...editing, itineraryText: e.target.value })} placeholder="9:30am | Pickup | Door-to-door collection" /></div>
              <div><Label>What's included (one per line)</Label>
                <Textarea rows={3} value={editing.includesText} onChange={(e) => setEditing({ ...editing, includesText: e.target.value })} /></div>
              <div><Label>Good to know / exclusions</Label>
                <Textarea rows={2} value={editing.excludes} onChange={(e) => setEditing({ ...editing, excludes: e.target.value })} /></div>
              <div><Label>FAQs (one per line: question | answer)</Label>
                <Textarea rows={3} value={editing.faqsText} onChange={(e) => setEditing({ ...editing, faqsText: e.target.value })} /></div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} data-testid="tour-form-active" />
                  <Label>Visible on website</Label>
                </div>
                <Button onClick={save} disabled={saving} data-testid="save-tour-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing.id ? "Save Changes" : "Create Tour"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TourManagerSection;
